import { RootStackParamList } from '$tablet-app/src/app/components/PrimaryNavigator';
import { FeatureDomain } from '@akin-home/amplify';
import {
  createDrawerNavigator,
  DrawerNavigationOptions,
} from '@react-navigation/drawer';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import { drawerIcon } from '../../../components/DrawerIcon/DrawerIcon';
import { Panel } from '../../../components/Panel';
import { ScreenBackButton } from '../../../components/ScreenBackButton';
import { ScreenHeader } from '../../../components/ScreenHeader';
import { ScreenTitle } from '../../../components/ScreenTitle';
import { postPageViewLogEvent } from '../../../logging';
import { color, palette } from '../../../theme';
import { useCurrentUserID } from '../../../users';
import { combineDrawerNavigationOptions } from '../../functions';
import AvatarSelectScreen from '../AvatarSelectScreen';
import ContactUsScreen from '../ContactUsScreen';
import ProfileScreen from '../ProfileScreen';
import SettingsScreen from '../SettingsScreen';
import CustomDrawerContent from './SettingsDrawerContent';
import SettingsDrawerHeader from './SettingsDrawerHeader';

const Drawer = createDrawerNavigator();

type Props = NativeStackScreenProps<RootStackParamList, 'Settings'>;

interface OnFocusEventParams {
  page: string;
  feature_domain?: FeatureDomain;
  record_id?: string;
}

const SettingsNavigator = ({ navigation: { goBack } }: Props) => {
  const user_id = useCurrentUserID();

  const onFocusEvent =
    ({ page, feature_domain, record_id }: OnFocusEventParams) =>
    () =>
      postPageViewLogEvent({
        user_id,
        feature_domain,
        data: {
          page,
          record_id,
        },
      });

  const { width } = useWindowDimensions();

  const drawerContentOptions = useMemo(() => {
    const isSmallScreen = width < 1000;
    const sizeDependentOptions = isSmallScreen
      ? smallScreenDrawerOptions
      : largeScreenDrawerOptions;

    return combineDrawerNavigationOptions(
      defaultDrawerOptions,
      sizeDependentOptions
    );
  }, [width]);

  return (
    <Container>
      <Header>
        <ScreenBackButton onPress={goBack} />
        <ScreenTitle>App Settings</ScreenTitle>
      </Header>

      <BodyContainer>
        <Sheet>
          <Drawer.Navigator
            drawerContent={CustomDrawerContent}
            screenOptions={drawerContentOptions}
          >
            <Drawer.Screen
              name="My Profile"
              component={ProfileScreen}
              options={{
                drawerIcon: drawerIcon('user'),
                headerTitle: 'My Profile',
              }}
              listeners={({ route }) => ({
                focus: onFocusEvent({
                  page: route.name,
                }),
              })}
            />
            <Drawer.Screen
              name="Avatar"
              component={AvatarSelectScreen}
              options={{
                drawerIcon: drawerIcon('alien-monster'),
                headerTitle: 'Avatar',
              }}
              listeners={({ route }) => ({
                focus: onFocusEvent({
                  page: route.name,
                }),
              })}
            />

            <Drawer.Screen
              name="General Settings"
              component={SettingsScreen}
              options={{
                drawerIcon: drawerIcon('cog'),
                headerTitle: 'Settings',
              }}
              listeners={({ route }) => ({
                focus: onFocusEvent({
                  page: route.name,
                }),
              })}
            />
            <Drawer.Screen
              name="Contact Us"
              component={ContactUsScreen}
              options={{
                drawerIcon: drawerIcon('envelope'),
                headerTitle: 'Contact Us',
              }}
              listeners={({ route }) => ({
                focus: onFocusEvent({
                  page: route.name,
                }),
              })}
            />
          </Drawer.Navigator>
        </Sheet>
      </BodyContainer>
    </Container>
  );
};

const defaultDrawerOptions: DrawerNavigationOptions = {
  drawerActiveBackgroundColor: '#F5F7F9',
  drawerInactiveTintColor: color.Dark_gray,
  drawerInactiveBackgroundColor: undefined,
  drawerItemStyle: {
    alignContent: 'center',
    justifyContent: 'flex-start',
    width: '95%',
    borderRadius: 10,
    paddingLeft: 10,
  },
  drawerLabelStyle: { fontWeight: 'bold', fontSize: 20 },
  drawerStyle: {
    borderColor: palette.white,
    borderWidth: 0,
    elevation: 10,
  },
  headerShown: true,
  header: SettingsDrawerHeader,
};

const smallScreenDrawerOptions: DrawerNavigationOptions = {
  drawerType: 'front',
};

const largeScreenDrawerOptions: DrawerNavigationOptions = {
  drawerType: 'permanent',
  drawerStyle: {
    width: '25%',
  },
};

const Container = styled.View`
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
  align-items: center;
`;

const BodyContainer = styled.View`
  padding: 20px;
  padding-top: 0;
  flex: 1;
`;

const Sheet = styled(Panel)`
  flex-direction: row;
  flex: 1;
  overflow: hidden;
`;

export default SettingsNavigator;
